import {
    Box,
    Button,
    Divider,
    Flex,
    Hide,
    Portal,
    Text,
    useToast,
} from '@chakra-ui/react'
import React, { useContext, useMemo, useRef, useState } from 'react'
import Lottie from 'react-lottie-player'

import buttonLoader from '@/animations/button_loading.json'
import { borderVariant21, thickBoxShadow } from '@/chakraTheme'
import Toast from '@/common/Toast'
import { feedbackTypes } from '@/common/UnitFeedback/_enums.js'
import DottedGrid from '@/components/Attendance/assets/dotted_grid.svg'
import { AuthContext } from '@/contexts/AuthContext.js'

import { mentorEmojiData, studentEmojiData } from '../_enums.js'
import { submitFeedback } from '../_services.js'
import EmojiSelector from './EmojiSelector.jsx'
import Header from './Header.jsx'

const Modal = ({ onClose, FeedbackEntry, feedbackType }) => {
    const containerRef = useRef()
    const toast = useToast()
    const feedbackChannel = new BroadcastChannel('feedback')

    const [selectedEmoji, setSelectedEmoji] = useState(null)
    const [hoveredEmoji, setHoveredEmoji] = useState(null)
    const [selectedCategories, setSelectedCategories] = useState([])
    const [selectedOptions, setSelectedOptions] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { kalviumKc } = useContext(AuthContext)

    // emojiData based on feedback type
    const emojiData =
        feedbackType === feedbackTypes.STUDENT
            ? studentEmojiData
            : mentorEmojiData

    const selectedEmojiData = useMemo(
        () => emojiData.find(emoji => emoji.name === selectedEmoji),
        [selectedEmoji, emojiData]
    )

    const hasCategories = selectedEmojiData?.hasCategories || false

    const modalHeight = useMemo(() => {
        if (!selectedEmoji) return '426px' // default height
        if (hasCategories) return selectedCategories.length ? '598px' : '426px'
        return '500px' // Options-only mode
    }, [selectedEmoji, hasCategories, selectedCategories.length])

    const handleSubmit = async () => {
        setIsSubmitting(true)

        try {
            const payload = {
                id: FeedbackEntry.id,
                emoji_selected: selectedEmoji,
                options_selected: [...selectedCategories, ...selectedOptions],
            }

            const result = await submitFeedback(payload, kalviumKc)

            if (result.success) {
                // send feedback-submission status to other tabs
                feedbackChannel.postMessage('feedbackSubmitted')
                toast({
                    duration: 5000,
                    position: 'top-right',
                    render: props => (
                        <Toast
                            status={'success'}
                            title={
                                <Text>
                                    Thank you, we’ve received your feedback.
                                </Text>
                            }
                            {...props}
                        />
                    ),
                })
                onClose()
            }
        } catch (error) {
            toast({
                duration: 5000,
                position: 'top-right',
                render: props => (
                    <Toast
                        status={'error'}
                        title={<Text>Error submitting feedback.</Text>}
                        {...props}
                    />
                ),
            })
            onClose()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <Hide below="lg">
                <Portal containerRef={containerRef}>
                    <Box
                        position="fixed"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        bg="rgba(0, 0, 0, 0.5)"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        zIndex={2000}
                    >
                        <Box
                            width="928px"
                            height={modalHeight}
                            padding={6}
                            backgroundImage={`url(${DottedGrid})`}
                            border={borderVariant21}
                            boxShadow={thickBoxShadow}
                            pointerEvents={isSubmitting ? 'none' : 'auto'}
                            display="flex"
                            flexDirection="column"
                            transition={'all 0.3s ease'}
                        >
                            <Header FeedbackEntry={FeedbackEntry} />
                            <Divider borderColor="silver2" mt={3} />
                            <EmojiSelector
                                emojiData={emojiData}
                                selectedEmojiData={selectedEmojiData}
                                hasCategories={hasCategories}
                                selectedEmoji={selectedEmoji}
                                hoveredEmoji={hoveredEmoji}
                                selectedCategories={selectedCategories}
                                selectedOptions={selectedOptions}
                                setHoveredEmoji={setHoveredEmoji}
                                setSelectedEmoji={setSelectedEmoji}
                                setSelectedCategories={setSelectedCategories}
                                setSelectedOptions={setSelectedOptions}
                            />
                            <Flex flex="1" />
                            {selectedEmoji && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    mt={2}
                                >
                                    <Button
                                        variant={'pressable-small'}
                                        isDisabled={
                                            !selectedEmoji || isSubmitting
                                        }
                                        onClick={handleSubmit}
                                        sx={{
                                            _hover: {
                                                boxShadow: 'none',
                                            },
                                            _active: {
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <Lottie
                                                loop
                                                animationData={buttonLoader}
                                                play={true}
                                            />
                                        ) : (
                                            'Submit'
                                        )}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Portal>
            </Hide>

            <Flex ref={containerRef} />
        </>
    )
}

export default Modal
